// ContactPage.jsx
import React, { useState } from "react";
import { Helmet } from "react-helmet";
import { motion, AnimatePresence } from "framer-motion";
import {
	MapPin,
	Phone,
	Mail,
	Calendar,
	Clock,
	ExternalLink,
} from "lucide-react";
import { PopupModal } from "react-calendly";
import ContactForm from "../../components/contact/ContactForm";
import GoogleMap from "../../components/GoogleMap";

const InfoCard = ({ icon: Icon, title, children }) => (
	<div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:border-primary/20 transition-colors">
		<div className="flex flex-col items-center text-center gap-4">
			<div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
				<Icon className="w-6 h-6 text-primary" />
			</div>
			<div>
				<h3 className="font-medium text-lg mb-2">{title}</h3>
				<div className="text-gray-600 space-y-1">{children}</div>
			</div>
		</div>
	</div>
);
const ContactPage = () => {
	const [openBooking, setOpenBooking] = useState(false);
	const [openContactForm, setOpenContactForm] = useState(false);

	// Animation constants matching home page
	const bounceTransition = {
		duration: 0.4,
		ease: [0.68, -0.25, 0.265, 1.25],
	};

	return (
		<>
			<Helmet>
				<link rel="canonical" href={`https://offcutshades.com/contact`} />
				<title>
					Contact Us: Speak with Experts in Window Shades and Blinds | Off Cut
					Shades
				</title>
				<meta
					name="description"
					content="Speak with custom shade and blind experts here at Off Cut Shades. Our professional team can help you choose the right window shades for your home."
				/>
				<script type="application/ld+json">
					{`{
						"@context": "https://schema.org",
						"@type": "BreadcrumbList",
						"itemListElement": [
							{
							"@type": "ListItem",
							"position": 1,
							"name": "Home",
							"item": "https://offcutshades.com"
							},
							{
							"@type": "ListItem",
							"position": 2,
							"name": "Contact Us",
							"item": "https://offcutshades.com/contact"
							}
						]
						}`}
				</script>
			</Helmet>
			<main className="bg-gray-50 min-h-screen py-12">
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
					<motion.div
						className="relative text-center max-w-3xl mx-auto mb-16 z-10"
						initial={{ opacity: 0, y: -20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={bounceTransition}
					>
						<motion.h1
							className="text-5xl md:text-7xl font-bold mb-6 leading-tight"
							initial={{ opacity: 0, y: 30 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ ...bounceTransition, delay: 0.2 }}
						>
							Contact Us
						</motion.h1>
						<motion.p
							className="text-xl text-gray-600"
							initial={{ opacity: 0, y: 20 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ ...bounceTransition, delay: 0.3 }}
						>
							Get in touch with our team of window treatment experts. We’re here
							to help you find the perfect window shades and blinds to enhance
							your space.
						</motion.p>
					</motion.div>

					{/* Contact Info Cards */}
					<div className="grid md:grid-cols-3 gap-8 mb-16">
						<InfoCard icon={MapPin} title="Visit Us">
							<p>50 Hollinger Road</p>
							<p>Toronto, ON M4B 3G5</p>
							<p>Canada</p>
						</InfoCard>

						<InfoCard icon={Phone} title="Call Us">
							<p>416-266-3501</p>
							<p>800-668-1728</p>
							<p className="text-sm text-gray-500">
								Mon - Fri, 8:30am - 5:00pm ET
							</p>
						</InfoCard>

						<InfoCard icon={Mail} title="Email Us">
							<a
								href="mailto:info@offcutshades.com"
								className="text-primary hover:underline"
							>
								info@offcutshades.com
							</a>
							<p className="text-sm text-gray-500">
								We typically respond within 24 hours
							</p>
						</InfoCard>
					</div>

					{/* Showroom Section */}
					<div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-16">
						<div className="grid md:grid-cols-2 gap-8">
							<div className="p-8">
								<h2 className="text-2xl font-bold text-gray-900 mb-4">
									Visit Our Showroom
								</h2>
								<div className="prose text-gray-600">
									<p>
										Experience our window treatments in person at our Toronto
										showroom. Our expert staff is ready to help you find the
										perfect window shade solution for your home.
									</p>

									<div className="mt-8">
										<h3 className="text-lg font-medium mb-4">
											Hours of Operation (walk-ins are welcome!)
										</h3>
										<div className="space-y-2">
											{[
												"Monday",
												"Tuesday",
												"Wednesday",
												"Thursday",
												"Friday",
											].map((day) => (
												<div key={day} className="flex justify-between">
													<span>{day}</span>
													<span>8:30 AM - 5:00 PM</span>
												</div>
											))}
											{["Saturday", "Sunday"].map((day) => (
												<div
													key={day}
													className="flex justify-between text-gray-400"
												>
													<span>{day}</span>
													<span>Closed</span>
												</div>
											))}
										</div>
									</div>

									<div className="mt-8 flex gap-4">
										<button
											onClick={() => setOpenBooking(true)}
											className="flex-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors flex items-center justify-center gap-2"
										>
											<Calendar className="w-5 h-5" />
											Book Consultation
										</button>
										<button
											onClick={() => setOpenContactForm(true)}
											className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center justify-center gap-2"
										>
											<Mail className="w-5 h-5" />
											Email Us
										</button>
									</div>
								</div>
							</div>
							<div className="relative h-full min-h-[400px]">
								<img
									src="/images/showroom/1.jpg"
									alt="Our Showroom"
									className="absolute inset-0 w-full h-full object-cover"
								/>
							</div>
						</div>
					</div>

					{/* Map Section */}
					<div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
						<div className="p-8">
							<h2 className="text-2xl font-bold text-gray-900 mb-4">Find Us</h2>
							<div className="h-[400px] rounded-lg overflow-hidden">
								<GoogleMap />
							</div>
						</div>
					</div>
				</div>

				{/* Calendly Modal */}
				<PopupModal
					url="https://calendly.com/offcutshades"
					rootElement={document.getElementById("root")}
					open={openBooking}
					onModalClose={() => setOpenBooking(false)}
					text="Schedule a Consultation"
					textColor="#ffffff"
					color="#00a2ff"
				/>

				{/* Contact Form Modal */}
				<AnimatePresence>
					{openContactForm && (
						<motion.div
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							exit={{ opacity: 0 }}
							className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
							onClick={() => setOpenContactForm(false)}
						>
							<motion.div
								initial={{ scale: 0.95 }}
								animate={{ scale: 1 }}
								exit={{ scale: 0.95 }}
								onClick={(e) => e.stopPropagation()}
								className="bg-white rounded-xl w-full max-w-lg p-6"
							>
								<ContactForm landing={false} />
							</motion.div>
						</motion.div>
					)}
				</AnimatePresence>
			</main>
		</>
	);
};

export default ContactPage;
